/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import { get } from 'lodash';
import { isBefore, getDay, parseISO, endOfYesterday } from 'date-fns';
import { AttributesGroup, RenderMapper, FormData } from '../../types';
import { getCustomRepeatMessage, getQuickSuggestionsMessage } from '../../../../../components/recurrence/suggestions';
import { PeriodOptions, Layout } from '../../constants';

export const groupAttributes: AttributesGroup = {
  name: 'schedule',
  label: 'Schedule',
  attributes: [
    'startDate',
    'endDate',
    'repeatOption',
    'repeatInterval',
    'period',
    'repeatOn',
    'endDateMessage',
    'stopRepeating',
    'stopOn',
    'repeatTimes',
    'scheduleMessage',
  ],
};

export const mapper: RenderMapper = {
  repeatOption: {
    renderInfo: {
      noLabel: true,
      ...Layout.Columns.Full,
      ...Layout.Presenter.TwoThirds,
      conditionalRender({ formData }: { formData: FormData }): boolean {
        return !!get(formData, 'endDate');
      },
      presenterAttributes({ formData }) {
        return {
          allowEmpty: false,
          formData,
          moveUp: true,
        };
      },
    },
  },
  repeatInterval: {
    renderInfo: {
      noLabel: true,
      customClass: 'pl-2 ml-1 mr-2',
      conditionalRender({ formData }: { formData: FormData }): boolean {
        return get(formData, 'repeatOption.value') === PeriodOptions.Custom && get(formData, 'endDate');
      },
      presenterAttributes({ field }) {
        return {
          preInputLabel: field.displayName,
        };
      },
    },
  },
  period: {
    renderInfo: {
      noLabel: true,
      ...Layout.Columns.TwoThirds,
      ...Layout.Presenter.Quarter,
      conditionalRender({ formData }: { formData: FormData }): boolean {
        return get(formData, 'repeatOption.value') === PeriodOptions.Custom;
      },
      presenterAttributes({ formData }) {
        return {
          allowEmpty: false,
          formData,
        };
      },
    },
    triggerUpdates({ updater }) {
      updater('repeatOn', null);
    },
  },
  days: {
    renderInfo: {
      ...Layout.Columns.Full,
      noLabel: true,
      conditionalRender(): boolean {
        return false;
      },
    },
  },
  repeatOn: {
    renderInfo: {
      ...Layout.Columns.Full,
      ...Layout.Presenter.Half,
      noLabel: true,
      conditionalRender({ formData }: { formData: FormData }): boolean {
        return get(formData, 'repeatOption.value') === PeriodOptions.Custom;
      },
      presenterAttributes({ formData, field }) {
        return {
          preInputLabel: field.displayName,
          allowEmpty: false,
          formData,
        };
      },
    },
  },
  startDate: {
    renderInfo: {
      ...Layout.Columns.Third,
      formGroupAttributes: {
        required: false,
      },
      presenterAttributes({ formData }: { formData: FormData }): any {
        return {
          disabledDate(date: Date): boolean {
            const isPast = isBefore(date, endOfYesterday());
            if (formData.days) {
              return !formData.days.includes(getDay(date)) || isPast;
            }
            return isPast;
          },
        };
      },
    },
    triggerUpdates({ formData, updater }) {
      const startDate = parseISO(get(formData, 'startDate'));
      const endDate = parseISO(get(formData, 'endDate'));
      if (startDate > endDate) {
        updater('endDate', null);
      }
    },
  },
  endDate: {
    renderInfo: {
      ...Layout.Columns.Third,
      presenterAttributes({ formData }) {
        return {
          disabledDate(date: Date): boolean {
            return formData.startDate ? isBefore(date, parseISO(formData.startDate)) : false;
          },
        };
      },
    },
    triggerUpdates({ updater }) {
      updater('repeatOption');
    },
  },
  endDateMessage: {
    renderInfo: {
      ...Layout.Columns.Full,
      ...Layout.Presenter.Half,
      noLabel: true,
      presenterAttributes({ formData }: { formData: FormData }): any {
        const endDate = parseISO(get(formData, 'endDate'));
        let message = 'This task will not occur in months with fewer days than selected.';
        if (endDate.getMonth() === 1 && endDate.getDate() === 29) {
          message = '29th of February will be treated as 28th of February on non-leap years.';
        }
        return {
          message,
          variant: 'warning',
          moveUp: true,
        };
      },
      conditionalRender({ formData }: { formData: FormData }): boolean {
        const repeatOption = get(formData, 'repeatOption.value', '');
        if (repeatOption.includes('last day of the month')) {
          return true;
        }

        const endDate = parseISO(get(formData, 'endDate'));
        const repeatOn = get(formData, 'repeatOn.value');
        if (repeatOn && repeatOn.includes(endDate.getDate()))
          return (
            repeatOn &&
            repeatOn.includes(endDate.getDate()) &&
            (endDate.getDate() > 28 || (endDate.getMonth() === 1 && endDate.getDate() === 29))
          );
      },
    },
  },
  stopRepeating: {
    renderInfo: {
      noLabel: true,
      ...Layout.Columns.Third,
      conditionalRender({ formData }: { formData: FormData }): boolean {
        return get(formData, 'repeatOption.value', PeriodOptions.DoesNotRepeat) !== PeriodOptions.DoesNotRepeat;
      },
      presenterAttributes({ field }) {
        return {
          preInputLabel: field.displayName,
        };
      },
    },
  },
  stopOn: {
    renderInfo: {
      noLabel: true,
      ...Layout.Presenter.Half,
      ...Layout.Columns.TwoThirds,
      conditionalRender({ formData }: { formData: FormData }): boolean {
        return (
          get(formData, 'period.value') !== PeriodOptions.DoesNotRepeat && get(formData, 'stopRepeating.value') === 'On'
        );
      },
    },
  },
  repeatTimes: {
    renderInfo: {
      noLabel: true,
      ...Layout.Columns.TwoThirds,
      presenterAttributes({ formData }) {
        const pluralEnding = +formData.repeatTimes > 1 ? 's' : '';
        return {
          postInputLabel: 'Occurrence' + pluralEnding,
        };
      },
      conditionalRender({ formData }: { formData: FormData }): boolean {
        return (
          get(formData, 'period.value') !== PeriodOptions.DoesNotRepeat &&
          get(formData, 'stopRepeating.value') === 'After'
        );
      },
    },
  },
  scheduleMessage: {
    renderInfo: {
      customClass: 'col-md-12 d-flex align-self-start',
      noLabel: true,
      presenterAttributes({ formData }) {
        let repeat = get(formData, 'repeatOption.value', PeriodOptions.DoesNotRepeat);
        if (repeat === PeriodOptions.DoesNotRepeat) {
          return;
        }

        let message;
        if (repeat === PeriodOptions.Custom) {
          message = getCustomRepeatMessage(formData);
        } else {
          message = getQuickSuggestionsMessage(formData);
        }
        return {
          message,
        };
      },
    },
  },
};
